import { useState, useEffect } from "react";
import { H1Title, Paragraph } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import AuthGeneralInput from "../components/input/authinput";
import TeamService from "../services/teamService";
import { Success, Error as ToastError } from "../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faEnvelope, faTrash, faCheck, faTimes, faCopy } from "@fortawesome/free-solid-svg-icons";

export default function Team() {
  const [team, setTeam] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamName, setTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadTeam();
    loadInvites();
  }, []);

  const loadTeam = async () => {
    try {
      const { data } = await TeamService.getMyTeam();
      setTeam(data.data.team);
    } catch { /* no team yet */ }
    finally { setLoading(false); }
  };

  const loadInvites = async () => {
    try {
      const { data } = await TeamService.getInvites();
      setInvites(data.data.invites || []);
    } catch { /* no invites */ }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return;
    setCreating(true);
    try {
      const { data } = await TeamService.createTeam({ name: teamName });
      setTeam(data.data.team);
      Success('Team created!');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to create team');
    } finally { setCreating(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await TeamService.inviteMember(team._id, { email: inviteEmail });
      setTeam(data.data.team);
      setInviteEmail('');
      Success('Invitation sent!');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to invite');
    } finally { setInviting(false); }
  };

  const handleRespond = async (teamId, accept) => {
    try {
      await TeamService.respondToInvite(teamId, { accept });
      Success(accept ? 'Invite accepted!' : 'Invite declined');
      loadTeam();
      loadInvites();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to respond');
    }
  };

  const handleRemove = async (memberId) => {
    try {
      const { data } = await TeamService.removeMember(team._id, memberId);
      setTeam(data.data.team);
      Success('Member removed');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to remove');
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(team.inviteCode);
    Success('Invite code copied!');
  };

  if (loading) return <div className="flex justify-center py-20"><div className="text-grey-667">Loading...</div></div>;

  // Pending invites
  if (!team && invites.length > 0) {
    return (
      <div className="space-y-6 max-w-2xl">
        <H1Title size="sm">Team Invitations</H1Title>
        {invites.map(inv => (
          <div key={inv._id} className="bg-white rounded-xl border border-grey-200 p-5 flex items-center justify-between">
            <div>
              <p className="font-semibold">{inv.name}</p>
              <p className="text-sm text-grey-667">Led by {inv.leaderId.firstName} {inv.leaderId.lastName}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleRespond(inv._id, true)} className="bg-green text-white px-4 py-2 rounded-md text-sm cursor-pointer">
                <FontAwesomeIcon icon={faCheck} className="mr-1" /> Accept
              </button>
              <button onClick={() => handleRespond(inv._id, false)} className="bg-red-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer">
                <FontAwesomeIcon icon={faTimes} className="mr-1" /> Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // No team yet — create one
  if (!team) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faUsers} className="text-primary text-2xl" />
          </div>
          <H1Title size="sm">Create Your Team</H1Title>
          <Paragraph size="base" className="text-grey-667 mt-2">
            Teams can have up to 3 members. You can also submit individually.
          </Paragraph>
        </div>
        <form onSubmit={handleCreate} className="space-y-4">
          <AuthGeneralInput label="Team Name" name="teamName" placeholder="e.g. Impact Innovators" value={teamName} change={(e) => setTeamName(e.target.value)} />
          <AppButton type="submit" loading={creating}>Create Team</AppButton>
        </form>
      </div>
    );
  }

  // Team exists — manage it
  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <H1Title size="sm">{team.name}</H1Title>
        <button onClick={copyInviteCode} className="flex items-center gap-2 text-sm text-primary bg-primary-bg px-4 py-2 rounded-lg cursor-pointer hover:bg-primary-bg/80">
          <FontAwesomeIcon icon={faCopy} /> Code: {team.inviteCode}
        </button>
      </div>

      {/* Members */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold mb-4">Members ({team.members?.filter(m => m.status === 'accepted').length || 0}/3)</h3>
        <div className="space-y-3">
          {team.members?.map((member, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-grey-200 last:border-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-grey-200 rounded-full flex items-center justify-center text-sm font-bold text-grey-667">
                  {member.userId?.firstName?.[0]}{member.userId?.lastName?.[0]}
                </div>
                <div>
                  <p className="text-sm font-semibold">{member.userId?.firstName} {member.userId?.lastName}</p>
                  <p className="text-xs text-grey-667">{member.userId?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  member.status === 'accepted' ? 'bg-green-light text-green' :
                  member.status === 'invited' ? 'bg-yellow-500/10 text-orange-dark' :
                  'bg-red-500/10 text-red-500'
                }`}>
                  {member.role === 'lead' ? 'Leader' : member.status}
                </span>
                {member.role !== 'lead' && member.status !== 'declined' && (
                  <button onClick={() => handleRemove(member.userId?._id)} className="text-red-500 hover:text-red-600 cursor-pointer">
                    <FontAwesomeIcon icon={faTrash} size="sm" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invite */}
      {(team.members?.filter(m => m.status !== 'declined').length || 0) < 3 && (
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <h3 className="font-bold mb-2">Invite a Member</h3>
          <p className="text-xs text-grey-667 mb-4">All members must be registered in the portal to be added to your team.</p>
          <form onSubmit={handleInvite} className="flex gap-3">
            <div className="flex-1">
              <AuthGeneralInput type="email" name="inviteEmail" placeholder="Enter their email" value={inviteEmail} change={(e) => setInviteEmail(e.target.value)} required />
            </div>
            <AppButton type="submit" loading={inviting} fullWidth={false} className="px-6 self-end">
              <FontAwesomeIcon icon={faEnvelope} className="mr-1" /> Invite
            </AppButton>
          </form>
        </div>
      )}
    </div>
  );
}
